/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { ReactNode } from 'react'
import { useServiceContext } from '../../root'
import Loader from '../shared/Loader'
import { RcmPaginate } from '../rcm-table/RcmPaginate'
import { RcmArrowsPaginate } from '../rcm-table/RcmArrowsPaginate'
import LoadingSpinner from '../LoadingSpinner'

export enum TableSortDir {
  ASC = 'asc',
  DESC = 'desc'
}

export interface TableProps {
  className?: string
  noResultsText?: string
  disableOnInitLoad?: boolean
  loadData?: (params: { sortDir: TableSortDir; sort: string; perPage: number; page: number }) => void
  onSort?: (sortName: string, sortDir: 'asc' | 'desc') => void
  page?: number
  hasNextPage?: boolean
  defaultSortBy?: string
  defaultSortDesc?: boolean
  data?: any[]
  total?: number
  blocked?: boolean
  loading?: boolean
  persistanceKey?: string
  simplePaginate?: boolean
  hidePaginate?: boolean
  TableFooter?: ReactNode
  showTableBody?: boolean
  HeadRow?: ReactNode
  Row?: (props: { row: any; index: number }) => ReactNode
  rowKey?: string
  onRowEnter?: (row: any) => void
  onPageChange?: (params: { page: number; perPage: number }) => void
  [key: string]: any
}

export const Table: React.FC<TableProps> = ({
  className = '',
  noResultsText = 'NO_RESULTS_FILTERED',
  data = [],
  blocked = false,
  loading = false,
  hidePaginate = false,
  simplePaginate = false,
  page = 1,
  hasNextPage = false,
  total = 0,
  TableFooter,
  HeadRow,
  Row,
  defaultSortBy,
  defaultSortDesc,
  loadData,
  onSort: externalOnSort,
  showTableBody = true,
  ...props
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const getColumnCount = (headRow: ReactNode): number => {
    if (React.isValidElement(headRow) && headRow.props && (headRow as any).props.children) {
      if (Array.isArray((headRow as any).props.children)) {
        return (headRow as any).props.children.length
      }
      return 1
    }
    return 3
  }

  // Create onSort handler - this should match the signature expected by TableHeadRow
  const onSort = (sortName: string, sortDir: 'asc' | 'desc') => {
    // Use external onSort if provided (from RcmTable)
    if (externalOnSort) {
      externalOnSort(sortName, sortDir)
    } else if (loadData) {
      // Fallback to legacy loadData for backwards compatibility
      loadData({
        sort: sortName,
        sortDir: sortDir as TableSortDir,
        page: page,
        perPage: 10
      })
    }
  }

  return (
    <Loader blocking={blocked}>
      <div>
        <table className={`table ${className}`}>
          {HeadRow && (
            <thead>
              {React.cloneElement(HeadRow as React.ReactElement<any>, {
                sort: defaultSortBy,
                sortDir: defaultSortDesc ? TableSortDir.DESC : TableSortDir.ASC,
                onSort: onSort
              })}
            </thead>
          )}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={getColumnCount(HeadRow)} style={{ textAlign: 'center', padding: '40px' }}>
                  <LoadingSpinner size={40} centered={true} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                {showTableBody && (
                  <td colSpan={getColumnCount(HeadRow)} className="empty-records">
                    {translate(noResultsText)}
                  </td>
                )}
              </tr>
            ) : (
              data.map((row, index) =>
                Row ? (
                  Row({ row, index })
                ) : (
                  <tr key={row.id || index}>
                    <td>{JSON.stringify(row)}</td>
                  </tr>
                )
              )
            )}
          </tbody>
          {TableFooter && <tfoot>{TableFooter}</tfoot>}
        </table>
        {!hidePaginate && (simplePaginate ? <RcmArrowsPaginate /> : <RcmPaginate />)}
      </div>
    </Loader>
  )
}
